import {
  IconButton,
  type IconButtonProps,
} from "$components/button/icon-button";
import {
  Card,
  CardBody,
  CardColors,
  CardDetails,
  CardFooterItem,
  CardSubtitle,
  CardSummary,
  CardTitle,
} from "$components/card";
import { BinIcon, CheckIcon } from "$components/icons";
import { makeStyles } from "$components/mui-styles";
import { toast } from "$global-providers/toast-provider";
import type { PaymentStatus } from "$routes/billing/_lib/pre-service/payment-status";
import { PaymentStatusBadge } from "$routes/billing/_lib/pre-service/payment-status-badge";
import { gql, useMutation } from "@apollo/client";
import { Box, Grid } from "@material-ui/core";
import React from "react";
import {
  defineMessage,
  FormattedDate,
  FormattedMessage,
  FormattedTime,
  useIntl,
} from "react-intl";
import DeleteInvestigationModal from "./delete-investigation-modal";
import { requestedInvestigationTranslation } from "./intl-strings";
import type {
  FacilitySnapshot,
  Performer,
} from "./investigation-activity-type";
import RequestedBubble from "./requested-bubble";
import { investigationStatusStyles } from "./styles/common";
import { useInvestigationsCache } from "./use-investigations-cache";

export const MUTATION_DELETE_INVESTIGATION_ACTIVITIES = gql`
  mutation DeleteInvestigations($input: [ID!]!) {
    deleteInvestigations(input: $input) {
      success
      investigationActivityIds
    }
  }
`;

const deleteAria = defineMessage({
  id: "investigation.card.delete.aria",
  defaultMessage: "delete-investigation",
});

const BinButton = (props: IconButtonProps) => (
  <IconButton color="error" aria-label="delete" title="delete" {...props}>
    <BinIcon fill="currentColor" />
  </IconButton>
);

const useStyles = makeStyles((theme) => ({
  summaryContent: {
    margin: `${theme.spacing(2)}px 0`,
    flexDirection: "column",
    "&.Mui-expanded": {
      margin: `${theme.spacing(2)}px 0 ${theme.spacing(3)}px 0`,
    },
  },
  binButton: {
    margin: `${theme.spacing(-0.5)}px 0`,
  },
  ...investigationStatusStyles(theme),
}));

interface Props {
  defaultExpanded?: boolean;
  fulfilledDate?: string;
  fulfiller?: Performer;
  isRequested?: boolean;
  requestedDate?: string;
  requester?: Performer;
  title: string;
  subtitle?: string;
  requestLocation: FacilitySnapshot;
  outcomeLocation: FacilitySnapshot;
  showStatus?: boolean;
  investigationActivityId: string;
  enableDelete?: boolean;
  patientUuid: string;
  paymentStatus?: PaymentStatus | null;
}

const LocationFooterItem = ({ children }: { children: React.ReactNode }) => {
  return (
    <Grid container item xs={12}>
      <CardFooterItem>{children}</CardFooterItem>
    </Grid>
  );
};

const InvestigationActivityCard = ({
  children,
  title,
  subtitle,
  defaultExpanded,
  requestedDate,
  fulfiller,
  fulfilledDate,
  isRequested,
  requester,
  requestLocation,
  outcomeLocation,
  showStatus,
  investigationActivityId,
  enableDelete,
  patientUuid,
  paymentStatus,
}: WithChildren<Props>) => {
  const { formatMessage } = useIntl();
  const { removeInvestigationFromCache } = useInvestigationsCache(patientUuid);
  const [deleteInvestigationModalOpen, setDeleteInvestigationModalOpen] =
    React.useState(false);
  const [deleteInvestigations, { loading: deleteLoading }] = useMutation(
    MUTATION_DELETE_INVESTIGATION_ACTIVITIES,
    {
      onCompleted: (mutationResult) => {
        if (mutationResult.deleteInvestigations.success) {
          // We are only deleting one investigation at a time at this point
          const idToRemove =
            mutationResult.deleteInvestigations.investigationActivityIds[0];
          removeInvestigationFromCache(idToRemove);
          toast.success(
            formatMessage({
              id: "investigations.deletion.success",
              defaultMessage: "Successfully deleted investigation request",
            }),
          );
        } else {
          toast.error(
            formatMessage({
              id: "investigations.deletion.error.unknown",
              defaultMessage:
                "There was an unknown error deleting the investigation.",
            }),
          );
        }
      },
      onError: (error) => {
        toast.error(
          formatMessage(
            {
              id: "investigations.deletion.error",
              defaultMessage:
                "There was an error deleting the investigation: {error}",
            },
            { error: error.message },
          ),
        );
        setDeleteInvestigationModalOpen(false);
      },
    },
  );

  const classes = useStyles();

  return (
    <>
      <Card
        data-testid="investigation-card"
        color={CardColors.INVESTIGATION}
        expandable
        defaultExpanded={defaultExpanded}
      >
        <CardBody>
          <CardSummary id="investigation-header">
            <Box display="flex" alignItems="flex-start">
              <Box flex={1}>
                {subtitle && (
                  <CardSubtitle color={CardColors.INVESTIGATION} gutterBottom>
                    {subtitle}
                  </CardSubtitle>
                )}
                <CardTitle>{title}</CardTitle>
              </Box>
              <Box marginTop={0.5} marginRight={1}>
                <PaymentStatusBadge status={paymentStatus ?? null} />
              </Box>
              {showStatus &&
                (isRequested ? (
                  <RequestedBubble className={classes.requestedBubble}>
                    <strong>
                      {formatMessage(requestedInvestigationTranslation)}
                    </strong>
                  </RequestedBubble>
                ) : (
                  <Box marginTop={0.5}>
                    <CheckIcon className={classes.checkIcon} />
                  </Box>
                ))}
              {enableDelete && (
                <BinButton
                  data-testid="investigation-card-delete-button"
                  className={classes.binButton}
                  aria-label={formatMessage(deleteAria)}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevents card from expanding/closing
                    setDeleteInvestigationModalOpen(true);
                  }}
                />
              )}
            </Box>
          </CardSummary>
          <CardDetails>
            <Box mb={3}>{children}</Box>
            <Grid container>
              {requester ? (
                <Grid container item xs={12}>
                  <CardFooterItem>
                    <FormattedMessage
                      id="investigationActivity.request.performer"
                      defaultMessage="Requested by:"
                    />{" "}
                    {requester?.name ? `${requester.name}, ` : ""}
                    {requestedDate ? (
                      <>
                        <FormattedDate value={requestedDate} />{" "}
                        <FormattedTime value={requestedDate} />
                      </>
                    ) : null}
                  </CardFooterItem>
                </Grid>
              ) : null}
              <Grid container item xs={12}>
                {fulfiller ? (
                  <CardFooterItem>
                    <FormattedMessage
                      id="investigationActivity.outcome.performer"
                      defaultMessage="Submitted by:"
                    />{" "}
                    {fulfiller?.name
                      ? `${fulfiller.name}${fulfilledDate ? ", " : ""}`
                      : ""}
                    {fulfilledDate ? (
                      <>
                        <FormattedDate value={fulfilledDate} />{" "}
                        <FormattedTime value={fulfilledDate} />
                      </>
                    ) : null}
                  </CardFooterItem>
                ) : null}
              </Grid>
              {requestLocation && (
                <LocationFooterItem>
                  <FormattedMessage
                    id="investigationActivity.request.location"
                    defaultMessage="Request Location:"
                  />{" "}
                  {requestLocation.name}
                </LocationFooterItem>
              )}
              {outcomeLocation && (
                <LocationFooterItem>
                  <FormattedMessage
                    id="investigationActivity.outcome.location"
                    defaultMessage="Outcome Location:"
                  />{" "}
                  {outcomeLocation.name}
                </LocationFooterItem>
              )}
            </Grid>
          </CardDetails>
        </CardBody>
      </Card>

      <DeleteInvestigationModal
        onClose={() => {
          setDeleteInvestigationModalOpen(false);
        }}
        onSubmit={() =>
          deleteInvestigations({
            variables: { input: [investigationActivityId] },
          })
        }
        loading={deleteLoading}
        open={deleteInvestigationModalOpen}
        investigationName={title}
      />
    </>
  );
};

InvestigationActivityCard.defaultProps = {
  subtitle: null,
  requester: undefined,
  requestedDate: undefined,
  fulfiller: undefined,
  fulfilledDate: undefined,
  isRequested: false,
  defaultExpanded: false,
  showStatus: true,
  enableDelete: false,
};

export default InvestigationActivityCard;
